import React, { useState, useEffect } from 'react';
import { Plus, AlertCircle } from 'lucide-react';
import Layout from '../../component/layout';
import ApiConfig from '../../api/ApiConfig';
import Editac from '../../assets/images/bx_edit.svg';
import DelectAc from '../../assets/images/DelectAcBlc.svg';
import {
  getWithAuthCallWithErrorResponse,
  PostCallWithErrorResponse,
  deleteWithAuthCall,
} from '../../api/ApiServices';

// ─── AI Agent API Functions ───────────────────────────────────────────────────

async function fetchAllAiAgents() {
  return getWithAuthCallWithErrorResponse(ApiConfig.GET_ALL_AI_AGENTS);
}

async function createAiAgent(data) {
  return PostCallWithErrorResponse(ApiConfig.CREATE_AI_AGENT, JSON.stringify(data));
}

async function updateAiAgent(data) {
  const resp = await fetch(ApiConfig.UPDATE_AI_AGENT, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + localStorage.getItem('Token'),
    },
    body: JSON.stringify(data),
  });
  return resp.json();
}

async function fetchAiAgentById(agentId) {
  return getWithAuthCallWithErrorResponse(`${ApiConfig.GET_AI_AGENT_BY_ID}?agentId=${agentId}`);
}

async function deleteAiAgent(agentId) {
  return deleteWithAuthCall(`${ApiConfig.DELETE_AI_AGENT}?agentId=${agentId}`);
}

// ─── AI Agent Mapping API Functions ──────────────────────────────────────────

async function fetchAllAiAgentMappings() {
  return getWithAuthCallWithErrorResponse(ApiConfig.GET_ALL_AI_AGENT_MAPPINGS);
}

async function createAiAgentMapping(data) {
  return PostCallWithErrorResponse(ApiConfig.CREATE_AI_AGENT_MAPPING, JSON.stringify(data));
}

async function updateAiAgentMapping(data) {
  const resp = await fetch(ApiConfig.UPDATE_AI_AGENT_MAPPING, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + localStorage.getItem('Token'),
    },
    body: JSON.stringify(data),
  });
  return resp.json();
}

async function deleteAiAgentMapping(id) {
  return deleteWithAuthCall(`${ApiConfig.DELETE_AI_AGENT_MAPPING}?id=${id}`);
}

// ─── Employees by Type API Function ──────────────────────────────────────────

async function fetchEmployeesByType(type = 'AI') {
  return getWithAuthCallWithErrorResponse(`${ApiConfig.GET_EMPLOYEES_BY_TYPE}?type=${type}`);
}

// ─── Component ────────────────────────────────────────────────────────────────

const emptyAgentForm = {
  agentId: '',
  agentType: '',
  invokeUrl: '',
  secretKey: '',
  isActive: true,
  allowedIntents: [],
  aopRules: { prohibitedContent: [], maxInstructionLength: 500 },
  headers: [],
  payloadSchemaStr: '',
};

const AiSetting = () => {
  const [activeTab, setActiveTab] = useState('registries');

  const [agents, setAgents] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal States
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Agent Form State
  const [agentForm, setAgentForm] = useState(emptyAgentForm);
  const [prohibitedInput, setProhibitedInput] = useState('');
  const [intentInput, setIntentInput] = useState('');

  // Mapping Form State
  const [mappingForm, setMappingForm] = useState({ id: null, employeeId: '', agentId: '', isActive: true });

  // ─── Load Data on Mount ─────────────────────────────────────────────────────

  useEffect(() => {
    loadAgents();
    loadMappings();
    loadEmployees();
  }, []);

  async function loadAgents() {
    setLoading(true);
    try {
      const res = await fetchAllAiAgents();
      if (res?.json?.success) {
        setAgents(res.json.data || []);
      }
    } catch (err) {
      console.error('Failed to load AI agents', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadMappings() {
    try {
      const res = await fetchAllAiAgentMappings();
      if (res?.json?.success) {
        setMappings(res.json.data || []);
      }
    } catch (err) {
      console.error('Failed to load mappings', err);
    }
  }

  async function loadEmployees() {
    try {
      const res = await fetchEmployeesByType('AI');
      if (res?.json?.Success) {
        setEmployees(res.json.Data || []);
      }
    } catch (err) {
      console.error('Failed to load employees', err);
    }
  }

  // ─── Agent Handlers ─────────────────────────────────────────────────────────

  const mapAgentToForm = (data) => ({
    agentId: data.agentId || '',
    agentType: data.agentType || '',
    invokeUrl: data.invokeUrl || '',
    secretKey: data.secretKey || '',
    isActive: data.isActive ?? true,
    allowedIntents: data.allowedIntents || [],
    aopRules: {
      prohibitedContent: data.aopRules?.prohibitedContent || [],
      maxInstructionLength: data.aopRules?.maxInstructionLength ?? 500,
    },
    headers: data.headers || [],
    payloadSchemaStr: data.payloadSchema ? JSON.stringify(data.payloadSchema, null, 2) : '',
  });

  const handleOpenAgentModal = async (agent = null) => {
    setProhibitedInput('');
    setIntentInput('');
    if (agent) {
      // Show modal immediately with list data, then fetch full details
      setAgentForm(mapAgentToForm(agent));
      setIsEditing(true);
      setShowAgentModal(true);
      try {
        const res = await fetchAiAgentById(agent.agentId);
        if (res?.json?.success && res.json.data) {
          setAgentForm(mapAgentToForm(res.json.data));
        }
      } catch (err) {
        console.error('Failed to fetch agent details', err);
      }
    } else {
      setAgentForm(emptyAgentForm);
      setIsEditing(false);
      setShowAgentModal(true);
    }
  };

  // Header helpers
  const addHeader = () => setAgentForm(f => ({ ...f, headers: [...f.headers, { key: '', value: '' }] }));
  const removeHeader = (idx) => setAgentForm(f => ({ ...f, headers: f.headers.filter((_, i) => i !== idx) }));
  const updateHeader = (idx, field, val) =>
    setAgentForm(f => ({ ...f, headers: f.headers.map((h, i) => i === idx ? { ...h, [field]: val } : h) }));

  // Prohibited content helpers
  const addProhibited = () => {
    const val = prohibitedInput.trim();
    if (!val) return;
    setAgentForm(f => ({ ...f, aopRules: { ...f.aopRules, prohibitedContent: [...f.aopRules.prohibitedContent, val] } }));
    setProhibitedInput('');
  };
  const removeProhibited = (item) =>
    setAgentForm(f => ({ ...f, aopRules: { ...f.aopRules, prohibitedContent: f.aopRules.prohibitedContent.filter(x => x !== item) } }));

  // Allowed intents helpers
  const addIntent = () => {
    const val = intentInput.trim();
    if (!val) return;
    setAgentForm(f => ({ ...f, allowedIntents: [...f.allowedIntents, val] }));
    setIntentInput('');
  };
  const removeIntent = (item) =>
    setAgentForm(f => ({ ...f, allowedIntents: f.allowedIntents.filter(x => x !== item) }));

  const handleSaveAgent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let payloadSchema;
      try {
        payloadSchema = JSON.parse(agentForm.payloadSchemaStr);
      } catch {
        alert('Invalid JSON in Custom Payload Schema.');
        setSaving(false);
        return;
      }

      const payload = {
        agentId: agentForm.agentId,
        agentType: agentForm.agentType,
        invokeUrl: agentForm.invokeUrl,
        secretKey: agentForm.secretKey,
        isActive: agentForm.isActive,
        allowedIntents: agentForm.allowedIntents,
        aopRules: {
          prohibitedContent: agentForm.aopRules.prohibitedContent,
          maxInstructionLength: agentForm.aopRules.maxInstructionLength,
        },
        headers: agentForm.headers,
        payloadSchema,
      };

      if (isEditing) {
        const res = await updateAiAgent(payload);
        if (res?.success) {
          setShowAgentModal(false);
          await loadAgents();
        } else {
          alert(res?.message || 'Failed to update agent.');
        }
      } else {
        const res = await createAiAgent(payload);
        if (res?.json?.success) {
          setShowAgentModal(false);
          await loadAgents();
        } else {
          alert(res?.json?.message || 'Failed to create agent.');
        }
      }
    } catch (err) {
      console.error('Failed to save agent', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm(`Are you sure you want to delete AI Agent: ${agentId}?`)) return;
    try {
      const res = await deleteAiAgent(agentId);
      // deleteWithAuthCall returns JSON directly (not wrapped in { json })
      if (res?.success) {
        setAgents(prev => prev.filter(a => a.agentId !== agentId));
        setMappings(prev => prev.filter(m => m.agentId !== agentId));
      } else {
        alert(res?.message || 'Failed to delete agent.');
      }
    } catch (err) {
      console.error('Failed to delete agent', err);
    }
  };

  // ─── Mapping Handlers ───────────────────────────────────────────────────────

  const handleOpenMappingModal = (mapping = null) => {
    if (mapping) {
      setMappingForm({ ...mapping });
      setIsEditing(true);
    } else {
      setMappingForm({ id: 0, employeeId: '', agentId: agents.length > 0 ? agents[0].agentId : '', isActive: true });
      setIsEditing(false);
    }
    setShowMappingModal(true);
  };

  const handleSaveMapping = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        Id: mappingForm.id || 0,
        EmployeeId: parseInt(mappingForm.employeeId),
        AgentId: mappingForm.agentId,
        IsActive: mappingForm.isActive,
      };

      if (isEditing) {
        const res = await updateAiAgentMapping(payload);
        if (res?.success) {
          setMappings(mappings.map(m => m.id === mappingForm.id ? { ...m, ...mappingForm } : m));
        }
      } else {
        const res = await createAiAgentMapping(payload);
        if (res?.json?.success) {
          setMappings([...mappings, res.json.data]);
        }
      }
      setShowMappingModal(false);
    } catch (err) {
      console.error('Failed to save mapping', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async (id) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;
    try {
      const res = await deleteAiAgentMapping(id);
      if (res?.success) {
        setMappings(mappings.filter(m => m.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete mapping', err);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
    <Layout>

      <style>{`
        .ai-modal-backdrop { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(2px); z-index: 1040; display: flex; align-items: center; justify-content: center; }
        .ai-modal { background: white; width: 100%; max-width: 700px; border-radius: 12px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); display: flex; flex-direction: column; overflow: hidden; animation: aiModalIn 0.2s ease-out; }
        @keyframes aiModalIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .ai-modal-header { padding: 1rem 1.5rem; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
        .ai-modal-title { font-weight: 600; font-size: 1rem; color: #1e293b; margin: 0; }
        .ai-modal-body { max-height: 70vh; overflow-y: auto; padding: 1.5rem; }
        .section-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 1rem; margin-bottom: 1rem; }
        .form-switch .form-check-input { width: 2.5em; height: 1.25em; margin-top: 0; flex-shrink: 0; }
        .tag-badge { display: inline-flex; align-items: center; background: #eff6ff; color: #1d4ed8; border-radius: 9999px; padding: 0.2rem 0.6rem; font-size: 0.75rem; font-weight: 500; margin: 0.2rem; gap: 0.3rem; }
        .tag-badge button { background: none; border: none; padding: 0; color: #3b82f6; font-size: 1rem; line-height: 1; cursor: pointer; }
        .tag-badge button:hover { color: #ef4444; }
        .ai-status-active { background: #dcfce7; color: #166534; padding: 0.2rem 0.65rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500; }
        .ai-status-inactive { background: #f1f5f9; color: #64748b; padding: 0.2rem 0.65rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500; }
        .ai-type-badge { background: #eff6ff; color: #1d4ed8; padding: 0.2rem 0.65rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500; }
        .ai-intent-badge { background: #f0fdf4; color: #15803d; padding: 0.15rem 0.5rem; border-radius: 9999px; font-size: 0.7rem; font-weight: 500; }
      `}</style>

      {/* Header */}
      <div className="sub-header-role mb-4">
        <h6 className="mt-2">AI Settings</h6>
        {activeTab === 'registries' ? (
          <button className="create-btn-sub-header" onClick={() => handleOpenAgentModal()}>
            Add AI Agent
          </button>
        ) : (
          <button className="create-btn-sub-header" onClick={() => handleOpenMappingModal()}>
            Create Mapping
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="role-table-header">
        <div className="search-input-wrapper allAcordiance">
          <button
            className={activeTab === 'registries' ? 'active all-anc-btn-sub-header' : 'all-anc-btn-sub-header'}
            onClick={() => setActiveTab('registries')}
          >
            AI Agent Registries
          </button>
        </div>
        <div className="entries-wrapper myAccordiance">
          <button
            className={activeTab === 'mappings' ? 'active all-anc-btn-sub-header' : 'my-anc-btn-sub-header'}
            onClick={() => setActiveTab('mappings')}
          >
            Employee Mappings
          </button>
        </div>
      </div>

      {/* Tables */}
      <div className="table-wrapper" id="scrol-tblNtable-wrapper-af">

        {/* REGISTRIES TAB */}
        {activeTab === 'registries' && (
          <table>
            <thead>
              <tr className="firstTabelRow">
                <th>Sr No</th>
                <th>Agent ID</th>
                <th>Type</th>
                <th>Invoke URL</th>
                <th>Intents</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="table-row-custom"><td colSpan="7" className="text-center py-4">Loading...</td></tr>
              ) : agents.length === 0 ? (
                <tr className="table-row-custom"><td colSpan="7" className="text-center py-4">No AI Agents found.</td></tr>
              ) : agents.map((agent, indx) => (
                <tr className="table-row-custom" key={agent.agentId}>
                  <td>{indx + 1}</td>
                  <td style={{maxWidth:'160px', wordBreak:'break-all'}}>{agent.agentId}</td>
                  <td><span className="ai-type-badge">{agent.agentType || '—'}</span></td>
                  <td>
                    <span className="text-truncate d-inline-block" style={{maxWidth:'180px'}}>{agent.invokeUrl}</span>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {(agent.allowedIntents || []).slice(0, 2).map((intent, i) => (
                        <span key={i} className="ai-intent-badge">{intent}</span>
                      ))}
                      {(agent.allowedIntents || []).length > 2 && (
                        <span className="text-muted" style={{fontSize:'0.75rem'}}>+{agent.allowedIntents.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={agent.isActive ? 'ai-status-active' : 'ai-status-inactive'}>
                      {agent.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <img src={Editac} alt="Edit" style={{cursor:'pointer', marginRight:'8px'}} onClick={() => handleOpenAgentModal(agent)} />
                    <img src={DelectAc} alt="Delete" style={{cursor:'pointer'}} onClick={() => handleDeleteAgent(agent.agentId)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* MAPPINGS TAB */}
        {activeTab === 'mappings' && (
          <table>
            <thead>
              <tr className="firstTabelRow">
                <th>Sr No</th>
                <th>Internal Employee</th>
                <th>Assigned AI Agent</th>
                <th>Routing Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {mappings.length === 0 ? (
                <tr className="table-row-custom"><td colSpan="5" className="text-center py-4">No mappings found.</td></tr>
              ) : mappings.map((mapping, indx) => (
                <tr className="table-row-custom" key={mapping.id}>
                  <td>{indx + 1}</td>
                  <td>{employees.find(e => e.EmployeeId === mapping.employeeId)?.EmployeeName || `Employee #${mapping.employeeId}`}</td>
                  <td>{mapping.agentId}</td>
                  <td>
                    <span className={mapping.isActive ? 'ai-status-active' : 'ai-status-inactive'}>
                      {mapping.isActive ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td>
                    <img src={Editac} alt="Edit" style={{cursor:'pointer', marginRight:'8px'}} onClick={() => handleOpenMappingModal(mapping)} />
                    <img src={DelectAc} alt="Delete" style={{cursor:'pointer'}} onClick={() => handleDeleteMapping(mapping.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      </div>

      {/* --- MODALS --- */}

      {/* AI Agent Modal */}
      {showAgentModal && (
        <div className="ai-modal-backdrop">
          <div className="ai-modal">
            <form onSubmit={handleSaveAgent}>
              <div className="ai-modal-header">
                <h5 className="ai-modal-title">{isEditing ? 'Edit AI Agent' : 'Register AI Agent'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowAgentModal(false)}></button>
              </div>
              <div className="ai-modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Agent ID</label>
                    <input type="text" className="form-control" required
                      value={agentForm.agentId}
                      onChange={e => setAgentForm({...agentForm, agentId: e.target.value})}
                      readOnly={isEditing}
                      placeholder="e.g., HuskyVoice.AI" />
                    {isEditing && <div className="form-text text-warning"><AlertCircle size={12}/> Agent ID cannot be changed once created.</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Agent Type</label>
                    <input type="text" className="form-control" required
                      value={agentForm.agentType}
                      onChange={e => setAgentForm({...agentForm, agentType: e.target.value})}
                      placeholder="e.g., CX_AGENT" />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Invoke URL (Webhook)</label>
                  <input type="url" className="form-control" required
                    value={agentForm.invokeUrl}
                    onChange={e => setAgentForm({...agentForm, invokeUrl: e.target.value})}
                    placeholder="https://api..." />
                </div>

                <div className="mb-3">
                  <label className="form-label">Secret Key (Optional)</label>
                  <input type="text" className="form-control"
                    value={agentForm.secretKey}
                    onChange={e => setAgentForm({...agentForm, secretKey: e.target.value})}
                    placeholder="Provide API key if required by endpoint" />
                </div>

                <div className="section-box">
                  <label className="form-label fw-bold text-dark mb-3">Custom Headers</label>
                  {agentForm.headers.map((header, index) => (
                    <div key={index} className="d-flex gap-2 mb-2">
                      <input type="text" className="form-control form-control-sm"
                        placeholder="Key (e.g., Authorization)"
                        value={header.key}
                        onChange={e => updateHeader(index, 'key', e.target.value)} />
                      <input type="text" className="form-control form-control-sm"
                        placeholder="Value (e.g., Bearer ...)"
                        value={header.value}
                        onChange={e => updateHeader(index, 'value', e.target.value)} />
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeHeader(index)} title="Remove Header">
                        <img src={DelectAc} alt="Remove" style={{width:'14px', height:'14px'}} />
                      </button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-secondary mt-2 d-flex align-items-center gap-1" onClick={addHeader}>
                    <Plus size={14} /> Add Header
                  </button>
                </div>

                <div className="section-box">
                  <label className="form-label fw-bold text-dark mb-3">AOP Rules (Operating Procedures)</label>
                  <div className="mb-3">
                    <label className="form-label">Max Instruction Length</label>
                    <input type="number" className="form-control" required
                      value={agentForm.aopRules.maxInstructionLength}
                      onChange={e => setAgentForm({...agentForm, aopRules: {...agentForm.aopRules, maxInstructionLength: parseInt(e.target.value) || 0}})} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Prohibited Content</label>
                    <div className="d-flex gap-2 mb-2">
                      <input type="text" className="form-control form-control-sm"
                        value={prohibitedInput}
                        onChange={e => setProhibitedInput(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addProhibited())}
                        placeholder="e.g., financial requests" />
                      <button type="button" className="btn btn-sm btn-secondary" onClick={addProhibited}>Add</button>
                    </div>
                    <div>
                      {agentForm.aopRules.prohibitedContent.map((item, idx) => (
                        <span key={idx} className="tag-badge">
                          {item} <button type="button" onClick={() => removeProhibited(item)}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold text-dark">Allowed Intents</label>
                  <div className="d-flex gap-2 mb-2">
                    <input type="text" className="form-control form-control-sm"
                      value={intentInput}
                      onChange={e => setIntentInput(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addIntent())}
                      placeholder="e.g., CX_FOLLOW_UP" />
                    <button type="button" className="btn btn-sm btn-secondary" onClick={addIntent}>Add</button>
                  </div>
                  <div>
                    {agentForm.allowedIntents.map((intent, idx) => (
                      <span key={idx} className="tag-badge">
                        {intent} <button type="button" onClick={() => removeIntent(intent)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold text-dark">Custom Payload Schema (JSON)</label>
                  <textarea className="form-control font-monospace" rows="6" required
                    value={agentForm.payloadSchemaStr}
                    onChange={e => setAgentForm({...agentForm, payloadSchemaStr: e.target.value})}
                    style={{fontSize: '0.8rem'}}
                    placeholder='{"agent_id": "string", ...}' />
                  <div className="form-text">This will be parsed and sent as a dynamic JSON object to your backend.</div>
                </div>

                <div className="d-flex align-items-center gap-2 mt-3">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input" type="checkbox" role="switch" id="agentActive"
                      checked={agentForm.isActive}
                      onChange={e => setAgentForm({...agentForm, isActive: e.target.checked})}
                      style={{cursor:'pointer'}} />
                  </div>
                  <label className="form-check-label mb-0" htmlFor="agentActive" style={{cursor:'pointer', fontSize:'0.875rem'}}>Agent is Active</label>
                </div>
              </div>
              <div className="lineSeperate"></div>
              <div className="d-flex justify-content-end align-items-center mainBtnsSub">
                <button type="button" className="btn-width cancelBtn mx-3" onClick={() => setShowAgentModal(false)}>Cancel</button>
                <button type="submit" className="btn-width saveBtn" disabled={saving}>{saving ? 'Saving...' : 'Save Agent'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Mapping Modal */}
      {showMappingModal && (
        <div className="ai-modal-backdrop">
          <div className="ai-modal" style={{maxWidth:'480px'}}>
            <form onSubmit={handleSaveMapping}>
              <div className="ai-modal-header">
                <h5 className="ai-modal-title">{isEditing ? 'Edit Task Routing' : 'Map Employee to Agent'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowMappingModal(false)}></button>
              </div>
              <div className="ai-modal-body">
                {isEditing && (
                  <div className="mb-3">
                    <label className="form-label">Mapping ID</label>
                    <input type="text" className="form-control bg-light" readOnly value={mappingForm.id} />
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Internal Employee</label>
                  <select className="form-select" required
                    value={mappingForm.employeeId}
                    onChange={e => setMappingForm({...mappingForm, employeeId: parseInt(e.target.value) || ''})}>
                    <option value="" disabled>Select an Employee</option>
                    {employees.map(emp => (
                      <option key={emp.EmployeeId} value={emp.EmployeeId}>{emp.EmployeeName}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Target AI Agent</label>
                  <select className="form-select" required
                    value={mappingForm.agentId}
                    onChange={e => setMappingForm({...mappingForm, agentId: e.target.value})}>
                    <option value="" disabled>Select an Agent</option>
                    {agents.map(a => (
                      <option key={a.agentId} value={a.agentId}>{a.agentId}</option>
                    ))}
                  </select>
                  {agents.length === 0 && <div className="form-text text-danger">Please register an AI Agent first.</div>}
                </div>
                <div className="d-flex align-items-center gap-2 mt-3">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input" type="checkbox" role="switch" id="mappingActive"
                      checked={mappingForm.isActive}
                      onChange={e => setMappingForm({...mappingForm, isActive: e.target.checked})}
                      style={{cursor:'pointer'}} />
                  </div>
                  <label className="form-check-label mb-0" htmlFor="mappingActive" style={{cursor:'pointer', fontSize:'0.875rem'}}>Enable Routing</label>
                </div>
              </div>
              <div className="lineSeperate"></div>
              <div className="d-flex justify-content-end align-items-center mainBtnsSub">
                <button type="button" className="btn-width cancelBtn mx-3" onClick={() => setShowMappingModal(false)}>Cancel</button>
                <button type="submit" className="btn-width saveBtn" disabled={agents.length === 0 || saving}>
                  {saving ? 'Saving...' : 'Save Mapping'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </Layout>
    </>
  );
};

export default AiSetting;
